import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FullscreenError from '@/components/utils/FullscreenError';
import {
  faSpinner,
  faArrowLeft,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faCalendarAlt,
  faIdCard,
  faFileText,
  faImage
} from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { TelegramMessage, TelegramMessageID } from '@/models';
import { TelegramMessageStatus } from '@/models';
export default function AdminTelegramMessageDetail() {
  const { messageId } = useParams<{ messageId: string }>();
  const [message, setMessage] = useState<TelegramMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);

  useEffect(() => {
    if (messageId) {
      fetchMessageDetails();
    }
  }, [messageId]);

  const fetchMessageDetails = async () => {
    if (!messageId) return;

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const response = await APIRoutes.getTelegramMessage(messageId as TelegramMessageID);
      setMessage(response);
    } catch (err) {
      console.error('Critical error loading Telegram message details:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Telegram-Nachrichten-Details');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: TelegramMessageStatus) => {
    switch (status) {
      case 'WAITING':
        return <FontAwesomeIcon icon={faClock} className="w-5 h-5 text-yellow-600" />;
      case 'SUCCESS':
        return <FontAwesomeIcon icon={faCheckCircle} className="w-5 h-5 text-green-600" />;
      case 'FAILED':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: TelegramMessageStatus) => {
    switch (status) {
      case 'WAITING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'SUCCESS':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'FAILED':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400';
    }
  };

  if (criticalError) {
    return (
      <FullscreenError
        title="Telegram-Nachricht nicht verfügbar"
        message={error || 'Die Telegram-Nachricht konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchMessageDetails}
        onBack={() => window.history.back()}
      />
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin/telegram-messages"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Telegram-Nachricht Details
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* Action buttons can be added here */}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && !criticalError && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {message && !loading && (
          <div className="space-y-6">
            {/* Status & Basic Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Status & Grundinformationen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <span className={`inline-flex items-center px-3 py-2 text-sm font-semibold rounded-full ${getStatusColor(message.status)}`}>
                    {getStatusIcon(message.status)}
                    <span className="ml-2">{message.status}</span>
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faIdCard} className="w-4 h-4 mr-1" />
                    Nachrichten-ID
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    {message.id}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faIdCard} className="w-4 h-4 mr-1" />
                    Chat ID
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    {message.chatID}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                    Erstellt
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {formatDate(message.created)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Versuch-Anzahl
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {message.sendTryCount}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                <FontAwesomeIcon icon={faFileText} className="w-5 h-5 mr-2" />
                Nachrichteninhalt
              </h2>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <pre className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap font-mono">
                  {message.content}
                </pre>
              </div>
            </div>

            {/* Embeds */}
            {message.embeds && message.embeds.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  <FontAwesomeIcon icon={faImage} className="w-5 h-5 mr-2" />
                  Eingebettete Inhalte ({message.embeds.length})
                </h2>
                <div className="space-y-4">
                  {message.embeds.map((embed, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                      {embed.title && (
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Titel:
                          </label>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {embed.title}
                          </div>
                        </div>
                      )}
                      {embed.description && (
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Beschreibung:
                          </label>
                          <div className="text-sm text-gray-900 dark:text-white">
                            {embed.description}
                          </div>
                        </div>
                      )}
                      {embed.url && (
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            URL:
                          </label>
                          <a
                            href={embed.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                          >
                            {embed.url}
                          </a>
                        </div>
                      )}
                      {embed.image && (
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Bild:
                          </label>
                          <img
                            src={embed.image.url}
                            alt="Embed image"
                            className="max-w-full h-auto rounded"
                          />
                        </div>
                      )}
                      {embed.thumbnail && (
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Vorschaubild:
                          </label>
                          <img
                            src={embed.thumbnail.url}
                            alt="Embed thumbnail"
                            className="max-w-xs h-auto rounded"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Delivery Information */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Versandinformationen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                    Versendet am
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {message.sentAt ? formatDate(message.sentAt) : 'Noch nicht versendet'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                    Zuletzt aktualisiert
                  </label>
                  <div className="text-sm text-gray-900 dark:text-white">
                    {message.updated ? formatDate(message.updated) : 'Nie aktualisiert'}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Information */}
            {message.lastError && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 mr-2 text-red-600" />
                  Fehlerinformationen
                </h2>
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                  <pre className="text-sm text-red-700 dark:text-red-400 whitespace-pre-wrap font-mono">
                    {message.lastError}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
